import React from 'react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Surface the error so it is visible in the browser console + deployment logs
    // (instead of a silent blank white screen)
    // eslint-disable-next-line no-console
    console.error('App crashed:', error, info?.componentStack);
  }

  handleReload = () => {
    // Wipe any cached service worker + full reload, in case the crash is
    // rooted in a stale cached bundle.
    try {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations().then((regs) => {
          regs.forEach((r) => r.unregister());
          window.location.reload(true);
        });
        return;
      }
    } catch (_e) { /* ignore */ }
    window.location.reload(true);
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    const msg = this.state.error?.message || 'Something unexpected happened.';
    return (
      <div
        data-testid="app-error-boundary"
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2rem',
          background: 'linear-gradient(135deg,#f0fdf4 0%,#ecfdf5 100%)',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        }}
      >
        <div
          style={{
            maxWidth: '520px',
            background: '#fff',
            borderRadius: '16px',
            padding: '2.5rem',
            boxShadow: '0 10px 40px rgba(0,0,0,0.08)',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              width: '64px',
              height: '64px',
              margin: '0 auto 1.5rem',
              borderRadius: '50%',
              background: 'linear-gradient(135deg,#22c55e,#16a34a)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '32px',
            }}
          >
            🏏
          </div>
          <h1 style={{ margin: '0 0 0.5rem', color: '#0f172a', fontSize: '1.5rem' }}>
            Cricket Drive hit a hiccup
          </h1>
          <p style={{ margin: '0 0 1.5rem', color: '#475569', fontSize: '0.95rem', lineHeight: 1.5 }}>
            We couldn't finish loading the page. This usually resolves with a refresh — if it keeps
            happening, please contact us at <a href="mailto:info@cricketdrive.org" style={{ color: '#16a34a' }}>info@cricketdrive.org</a>.
          </p>
          <details style={{ margin: '0 0 1.5rem', fontSize: '0.75rem', color: '#94a3b8' }}>
            <summary style={{ cursor: 'pointer' }}>Technical details</summary>
            <pre style={{ whiteSpace: 'pre-wrap', textAlign: 'left', marginTop: '0.5rem' }}>{msg}</pre>
          </details>
          <button
            onClick={this.handleReload}
            data-testid="error-boundary-reload"
            style={{
              background: 'linear-gradient(135deg,#22c55e,#16a34a)',
              color: '#fff',
              border: 'none',
              padding: '0.75rem 2rem',
              borderRadius: '8px',
              fontSize: '0.95rem',
              fontWeight: 600,
              cursor: 'pointer',
              boxShadow: '0 4px 14px rgba(34,197,94,0.35)',
            }}
          >
            Refresh the page
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
